"use client";

import { useInView, useMotionValue, useReducedMotion, useSpring } from "motion/react";
import { useEffect, useRef } from "react";

interface CountUpProps {
  value: number;
  /** Formats the in-flight value for display, e.g. formatINR. Receives fractional values — round inside. */
  format?: (value: number) => string;
  className?: string;
}

/**
 * Number that springs to its value: counts up the first time it scrolls into
 * view, then chases live value changes (calculator sliders). Server-renders
 * the final value so crawlers and reduced-motion users see the real figure.
 */
export function CountUp({ value, format = (n) => String(Math.round(n)), className }: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  const motionValue = useMotionValue(0);
  const spring = useSpring(motionValue, { stiffness: 110, damping: 26 });

  useEffect(() => {
    if (!isInView) return;
    if (prefersReducedMotion) {
      motionValue.jump(value);
      return;
    }
    motionValue.set(value);
  }, [isInView, value, prefersReducedMotion, motionValue]);

  useEffect(() => {
    return spring.on("change", (latest) => {
      if (ref.current) ref.current.textContent = format(latest);
    });
  }, [spring, format]);

  return (
    <span ref={ref} className={className}>
      {format(value)}
    </span>
  );
}
